import { useEffect, useState } from 'react';
import {
  BarChart3,
  TrendingUp,
  Package,
  Truck,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  PageHeader,
  MetricCard,
  KPIGrid,
} from '@/components/common';
import { analyticsApi, DashboardData, Recommendation } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';

// Chart colors
const STOCK_COLORS = {
  optimal: '#10B981',  // emerald-500
  low: '#F59E0B',      // amber-500
  stockout: '#EF4444', // red-500
  overstock: '#3B82F6', // blue-500
};

const PO_COLORS = ['#48B8F0', '#8F49DE', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#EC4899'];

const priorityColors: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  HIGH: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  MEDIUM: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  LOW: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

export default function Analytics() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashboardData, recommendationsData] = await Promise.all([
        analyticsApi.getDashboard(),
        analyticsApi.getRecommendations(),
      ]);
      setDashboard(dashboardData);
      setRecommendations(recommendationsData);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleActionRecommendation = async (id: string) => {
    try {
      await analyticsApi.markRecommendationActioned(id);
      toast.success('Recommendation marked as actioned');
      setRecommendations(recommendations.filter(r => r.id !== id));
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <PageHeader
          title="Analytics"
          description="Insights and recommendations for your supply chain"
          breadcrumbs={[{ label: 'Analytics' }]}
        />
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Analytics"
        description="Insights and recommendations for your supply chain"
        breadcrumbs={[{ label: 'Analytics' }]}
      />

      {/* Overview KPIs */}
      {dashboard && (
        <KPIGrid columns={4}>
          <MetricCard
            label="Total Items"
            value={dashboard.metrics.totalItems.toLocaleString()}
            icon={<Package className="h-5 w-5" />}
          />
          <MetricCard
            label="Inventory Value"
            value={formatCurrency(dashboard.metrics.totalInventoryValue)}
            icon={<BarChart3 className="h-5 w-5" />}
          />
          <MetricCard
            label="Active Shipments"
            value={dashboard.metrics.totalShipments}
            icon={<Truck className="h-5 w-5" />}
          />
          <MetricCard
            label="Active Alerts"
            value={dashboard.metrics.activeAlerts}
            icon={<AlertTriangle className="h-5 w-5" />}
            trend={dashboard.metrics.activeAlerts > 0 ? { direction: 'up', value: '', text: 'needs attention' } : undefined}
          />
        </KPIGrid>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Stock Distribution - Pie Chart */}
        {dashboard && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Stock Distribution
              </CardTitle>
              <CardDescription>Inventory status breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const stockData = [
                  { name: 'Optimal', value: dashboard.distributions.inventory.optimal, color: STOCK_COLORS.optimal },
                  { name: 'Low Stock', value: dashboard.distributions.inventory.low, color: STOCK_COLORS.low },
                  { name: 'Out of Stock', value: dashboard.distributions.inventory.stockout, color: STOCK_COLORS.stockout },
                  { name: 'Overstock', value: dashboard.distributions.inventory.overstock, color: STOCK_COLORS.overstock },
                ].filter(item => item.value > 0);

                const total = stockData.reduce((sum, item) => sum + item.value, 0);

                if (total === 0) {
                  return (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      No inventory data available
                    </div>
                  );
                }

                return (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={stockData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {stockData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number) => [value, 'Items']}
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}
              {/* Legend */}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: STOCK_COLORS.optimal }} />
                  <span className="text-sm">Optimal ({dashboard.distributions.inventory.optimal})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: STOCK_COLORS.low }} />
                  <span className="text-sm">Low ({dashboard.distributions.inventory.low})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: STOCK_COLORS.stockout }} />
                  <span className="text-sm">Out ({dashboard.distributions.inventory.stockout})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: STOCK_COLORS.overstock }} />
                  <span className="text-sm">Over ({dashboard.distributions.inventory.overstock})</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* PO Status Distribution - Bar Chart */}
        {dashboard && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Purchase Order Status
              </CardTitle>
              <CardDescription>Current PO pipeline</CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const poData = Object.entries(dashboard.distributions.purchaseOrders)
                  .map(([status, count]) => ({
                    name: status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()),
                    count: count as number,
                  }))
                  .filter(item => item.count > 0);

                if (poData.length === 0) {
                  return (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      No purchase order data available
                    </div>
                  );
                }

                return (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={poData} layout="vertical" margin={{ left: 20, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="hsl(var(--border))" />
                        <XAxis type="number" allowDecimals={false} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={100}
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tickLine={false}
                        />
                        <Tooltip
                          formatter={(value: number) => [value, 'Orders']}
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                          {poData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={PO_COLORS[index % PO_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Trend Chart - Inventory Value Over Time (Mock Data) */}
      {dashboard && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Inventory Value Trend
            </CardTitle>
            <CardDescription>Last 7 days inventory value movement</CardDescription>
          </CardHeader>
          <CardContent>
            {(() => {
              // Generate mock trend data based on current value
              const currentValue = dashboard.metrics.totalInventoryValue || 100000;
              const trendData = Array.from({ length: 7 }, (_, i) => {
                const daysAgo = 6 - i;
                const date = new Date();
                date.setDate(date.getDate() - daysAgo);
                // Add some variance to make it look realistic
                const variance = (Math.random() - 0.5) * 0.1;
                const value = currentValue * (0.95 + (i * 0.01) + variance);
                return {
                  date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
                  value: Math.round(value),
                };
              });

              return (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="date"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                        tickLine={false}
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                        tickLine={false}
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip
                        formatter={(value: number) => [`$${value.toLocaleString()}`, 'Value']}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#48B8F0"
                        strokeWidth={2}
                        dot={{ fill: '#48B8F0', strokeWidth: 2 }}
                        activeDot={{ r: 6, fill: '#48B8F0' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* AI Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            AI Recommendations
          </CardTitle>
          <CardDescription>Smart suggestions to optimize your supply chain</CardDescription>
        </CardHeader>
        <CardContent>
          {recommendations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CheckCircle className="h-12 w-12 text-emerald-500 mb-4" />
              <p className="text-lg font-medium">All caught up!</p>
              <p className="text-muted-foreground">No recommendations at this time.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recommendations.map((rec) => (
                <div
                  key={rec.id}
                  className="flex items-start justify-between gap-4 rounded-lg border p-4"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge className={priorityColors[rec.priority]}>{rec.priority}</Badge>
                      <span className="text-sm text-muted-foreground">{rec.type}</span>
                    </div>
                    <h4 className="font-medium">{rec.title}</h4>
                    <p className="text-sm text-muted-foreground">{rec.description}</p>
                    {rec.suggestedAction && (
                      <p className="text-sm text-primary">
                        Suggested: {rec.suggestedAction.action}
                        {rec.suggestedAction.quantity && ` (Qty: ${rec.suggestedAction.quantity})`}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleActionRecommendation(rec.id)}
                  >
                    Mark Done
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      {dashboard && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Recent POs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Purchase Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard.recentActivity.purchaseOrders.slice(0, 5).map((po) => (
                  <div key={po.id} className="flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium">{po.poNumber}</div>
                      <div className="text-muted-foreground">{po.supplier?.name}</div>
                    </div>
                    <Badge variant="outline">{po.status}</Badge>
                  </div>
                ))}
                {dashboard.recentActivity.purchaseOrders.length === 0 && (
                  <p className="text-sm text-muted-foreground">No recent purchase orders</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Shipments */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Shipments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard.recentActivity.shipments.slice(0, 5).map((shipment) => (
                  <div key={shipment.id} className="flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium">{shipment.shipmentNumber}</div>
                      <div className="text-muted-foreground">
                        {shipment.origin?.name} → {shipment.destination?.name}
                      </div>
                    </div>
                    <Badge variant="outline">{shipment.status}</Badge>
                  </div>
                ))}
                {dashboard.recentActivity.shipments.length === 0 && (
                  <p className="text-sm text-muted-foreground">No recent shipments</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Alerts */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Alerts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard.recentActivity.alerts.slice(0, 5).map((alert) => (
                  <div key={alert.id} className="flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium">{alert.title}</div>
                      <div className="text-muted-foreground">{alert.type}</div>
                    </div>
                    <Badge className={priorityColors[alert.severity]}>{alert.severity}</Badge>
                  </div>
                ))}
                {dashboard.recentActivity.alerts.length === 0 && (
                  <p className="text-sm text-muted-foreground">No recent alerts</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
